import React, { useEffect, useState } from "react";
import LinksComponent from "../../../components/Tenant/LinksComponent/LinksComponent";
import DataTableComponent from "../../../components/Tenant/DataTableComponent/DataTableComponent";
import {
    useBanScanStateMutation,
    useUnbanScanStateMutation,
    useFetchBannedScanStatesMutation,
} from "../../../features/tenant/qrCode/qrCodeApi";
import { useGetAllStateMutation } from "../../../features/tenant/locations/locations.api";
import { toast } from "react-hot-toast";

const BanStateAtScanning = () => {
    const [selectedState, setSelectedState] = useState("");
    const [statesList, setStatesList] = useState([]);
    const [bannedStates, setBannedStates] = useState([]);

    const [
        getAllState,
        {
            data: allStateData,
            isLoading: allStateLoading,
            error: allStateError,
        },
    ] = useGetAllStateMutation();

    const [
        banScanState,
        {
            data: banStateData,
            isLoading: banStateLoading,
            error: banStateError,
        },
    ] = useBanScanStateMutation();

    const [
        unbanScanState,
        {
            data: unbanStateData,
            isLoading: unbanStateLoading,
            error: unbanStateError,
        },
    ] = useUnbanScanStateMutation();

    const [
        fetchBannedScanStates,
        {
            data: bannedStatesData,
            isLoading: bannedStatesLoading,
            error: bannedStatesError,
        },
    ] = useFetchBannedScanStatesMutation();

    const columns = [
        {
            name: "SNo",
            cell: (row, idx) => Number(idx + 1),
            sortable: false,
            width: "80px",
        },
        {
            name: "State",
            cell: (row) => row.state,
            sortable: true,
            width: "200px",
        },
        {
            name: "Banned At",
            cell: (row) =>
                row.created_at
                    ? new Date(row.created_at).toLocaleString()
                    : "N/A",
            sortable: true,
            width: "200px",
        },
        {
            name: "Banned By",
            cell: (row) => row.created_by_name || "N/A",
            sortable: true,
            width: "180px",
        },
        {
            name: "Action",
            cell: (row) => (
                <button
                    className="px-4 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                    onClick={() => handleUnban(row.id)}
                    disabled={unbanStateLoading}
                >
                    {unbanStateLoading ? "Unbanning..." : "Unban"}
                </button>
            ),
            sortable: false,
            width: "150px",
        },
    ];

    // Fetch all states for dropdown
    useEffect(() => {
        getAllState({
            tenant_id: JSON.parse(sessionStorage.getItem("vendorData"))
                .tenant_id,
        });
    }, []);

    // Fetch banned states on mount
    useEffect(() => {
        fetchBannedStates();
    }, []);

    const fetchBannedStates = () => {
        fetchBannedScanStates({
            token: sessionStorage.getItem("access_token"),
            tenant_id: JSON.parse(sessionStorage.getItem("vendorData"))
                .tenant_id,
        });
    };

    // Handle state list response
    useEffect(() => {
        if (allStateData && allStateData.success) {
            setStatesList(allStateData.body || []);
        }
    }, [allStateData]);

    useEffect(() => {
        if (allStateError) {
            toast.error("Error fetching states list");
        }
    }, [allStateError]);

    // Handle banned states response
    useEffect(() => {
        if (bannedStatesData && bannedStatesData.success) {
            setBannedStates(bannedStatesData.body?.bannedStates || []);
        }
    }, [bannedStatesData]);

    useEffect(() => {
        if (bannedStatesError) {
            toast.error("Error fetching banned states");
        }
    }, [bannedStatesError]);

    // Handle ban response
    useEffect(() => {
        if (banStateData) {
            if (banStateData.success) {
                toast.success(
                    banStateData.message || "State banned successfully"
                );
                setSelectedState("");
                fetchBannedStates();
            } else {
                toast.error(banStateData.message || "Error banning state");
            }
        }
    }, [banStateData]);

    useEffect(() => {
        if (banStateError) {
            toast.error(
                banStateError?.data?.message || "Error banning state"
            );
        }
    }, [banStateError]);

    // Handle unban response
    useEffect(() => {
        if (unbanStateData) {
            if (unbanStateData.success) {
                toast.success(
                    unbanStateData.message || "State unbanned successfully"
                );
                fetchBannedStates();
            } else {
                toast.error(
                    unbanStateData.message || "Error unbanning state"
                );
            }
        }
    }, [unbanStateData]);

    useEffect(() => {
        if (unbanStateError) {
            toast.error(
                unbanStateError?.data?.message || "Error unbanning state"
            );
        }
    }, [unbanStateError]);

    const handleBanState = () => {
        if (!selectedState) {
            toast.error("Please select a state to ban");
            return;
        }

        banScanState({
            token: sessionStorage.getItem("access_token"),
            tenant_id: JSON.parse(sessionStorage.getItem("vendorData"))
                .tenant_id,
            body: {
                state: selectedState,
            },
        });
    };

    const handleUnban = (id) => {
        unbanScanState({
            token: sessionStorage.getItem("access_token"),
            tenant_id: JSON.parse(sessionStorage.getItem("vendorData"))
                .tenant_id,
            body: {
                id: id,
            },
        });
    };

    return (
        <>
            <div className="flex w-full bg-gray-200 h-full justify-center">
                <div className="w-full">
                    <div className="border-b bg-white border-gray-300 pl-6 py-2 shadow-sm text-xl font-bold">
                        Ban State at Scanning
                        <LinksComponent />
                    </div>

                    <div className="p-3">
                        <div className="bg-white shadow rounded-lg mb-4">
                            <div className="border-b border-gray-300 bg-gray-50 rounded-t-lg p-3">
                                <h3 className="text-lg font-semibold mb-3">
                                    Ban a State from QR Scanning
                                </h3>
                                <div className="flex space-x-4 items-end">
                                    <div className="flex-1">
                                        <label className="block text-sm font-medium capitalize leading-6 text-gray-900 mb-1">
                                            Select State
                                        </label>
                                        <select
                                            className="w-full px-3 py-2 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500"
                                            value={selectedState}
                                            onChange={(e) =>
                                                setSelectedState(
                                                    e.target.value
                                                )
                                            }
                                            disabled={allStateLoading}
                                        >
                                            <option value="">
                                                {allStateLoading
                                                    ? "Loading states..."
                                                    : "Select a State"}
                                            </option>
                                            {statesList.map((s) => (
                                                <option
                                                    key={s.id}
                                                    value={s.state}
                                                >
                                                    {s.state}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <button
                                            className="btn btn-outline-primary px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                                            onClick={handleBanState}
                                            disabled={banStateLoading}
                                        >
                                            {banStateLoading
                                                ? "Banning..."
                                                : "Ban State"}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white shadow rounded-lg">
                            <div className="border-b border-gray-300 bg-gray-50 rounded-t-lg p-3">
                                <h3 className="text-lg font-semibold">
                                    Banned States List
                                </h3>
                            </div>
                            <DataTableComponent
                                keyField={(row) => row.id}
                                columns={columns}
                                data={bannedStates}
                                loading={bannedStatesLoading}
                                totalRows={bannedStates.length}
                                handlePerRowsChange={() => {}}
                                handlePageChange={() => {}}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default BanStateAtScanning;